import React, { useEffect, useState } from 'react'
import { Dialog } from 'primereact/dialog'
import { ExamApi, StarterHistoryItemDto } from '../../../api'
import { authState } from '../../../state/Auth.state'

type OverlayMode = 'horse' | 'rider' | 'show'

interface Props {
  registerId: string
  mode: OverlayMode
  name: string
  showId?: number | null
  showName?: string | null
  horseRegisterId?: string | null
  riderRegisterId?: string | null
  onHide: () => void
  onNavigateToExam: (showId: number, examId: number) => void
  onOpenOther: (registerId: string, mode: OverlayMode, name: string) => void
}

export const StarterHistoryOverlay = ({ registerId, mode, name, showId, showName, horseRegisterId, riderRegisterId, onHide, onNavigateToExam, onOpenOther }: Props) => {
  const [items, setItems] = useState<StarterHistoryItemDto[]>([])
  const [loading, setLoading] = useState(true)
  const [activeShowId, setActiveShowId] = useState<number | null | undefined>(showId)
  const [activeShowName, setActiveShowName] = useState<string | null | undefined>(showName)

  useEffect(() => {
    setLoading(true)
    const api = new ExamApi(authState.apicfg())
    const params = mode === 'horse'
      ? { horseRegisterId: registerId, showId: activeShowId ?? undefined }
      : mode === 'rider'
      ? { riderRegisterId: registerId, showId: activeShowId ?? undefined }
      : { showId: activeShowId ?? undefined, horseRegisterId: horseRegisterId ?? undefined, riderRegisterId: riderRegisterId ?? undefined }
    api.apiExamHistoryGet(params)
      .then((data: StarterHistoryItemDto[]) => setItems(data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false))
  }, [registerId, mode, activeShowId])

  const riderName = (item: StarterHistoryItemDto) =>
    [item.riderFirstName, item.riderLastName].filter(Boolean).join(' ')

  return (
    <Dialog
      header={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', paddingRight: '2rem' }}>
          <div>
            <div>{name}</div>
            {mode === 'horse' && items.length > 0 && (
              <div style={{ fontSize: '0.78rem', color: '#888', fontWeight: 400 }}>
                <div>
                  {items[0].horseGender ?? ''}
                  {items[0].horseGender && (items[0].horseBirthday || items[0].horseBreed) ? ' · ' : ''}
                  {items[0].horseBirthday ? new Date(items[0].horseBirthday).getFullYear() : ''}
                  {items[0].horseBirthday && items[0].horseBreed ? ' · ' : ''}
                  {items[0].horseBreed ?? ''}
                </div>
                {(items[0].horseFatherName || items[0].horseMotherName) && (
                  <div>
                    {items[0].horseFatherName ?? ''}
                    {items[0].horseFatherName && items[0].horseMotherName ? ' x ' : ''}
                    {items[0].horseMotherName ?? ''}
                  </div>
                )}
              </div>
            )}
          </div>
          {activeShowName && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.82rem', color: '#555', fontWeight: 400 }}>
              <span>{activeShowName}</span>
              <button
                onClick={() => { setActiveShowId(null); setActiveShowName(null) }}
                style={{ background: 'none', border: '1px solid #ccc', borderRadius: '4px',
                         cursor: 'pointer', fontSize: '0.82rem', padding: '0.1rem 0.35rem',
                         lineHeight: 1, color: '#666' }}
              >&#x2715;</button>
            </div>
          )}
        </div>
      }
      visible
      onHide={onHide}
      style={{ width: '95vw', maxWidth: '720px' }}
      contentStyle={{ padding: '0.5rem' }}
      dismissableMask
    >
      {loading && <div style={{ padding: '1rem', color: '#888' }}>Wird geladen ...</div>}

      {!loading && items.length === 0 && (
        <div style={{ padding: '1rem', color: '#888' }}>Keine Eintr&#228;ge gefunden.</div>
      )}

      {!loading && items.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #ccc', textAlign: 'left' }}>
                <th style={{ padding: '0.2rem 0.4rem' }}>St.Nr.</th>
                <th style={{ padding: '0.2rem 0.4rem' }}>Pl.</th>
                <th style={{ padding: '0.2rem 0.4rem' }}>IGV</th>
                <th style={{ padding: '0.2rem 0.4rem' }}>Pferd</th>
                <th style={{ padding: '0.2rem 0.4rem' }}>Reiter</th>
                <th style={{ padding: '0.2rem 0.4rem' }}>Pr&#252;fung</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <tr key={idx} style={{ borderTop: idx > 0 ? '1px solid #eee' : undefined }}>
                  <td style={{ padding: '0.25rem 0.4rem' }}>{item.startNo ?? '-'}</td>
                  <td style={{ padding: '0.25rem 0.4rem' }}>{item.place ? item.place : ''}</td>
                  <td style={{ padding: '0.25rem 0.4rem' }}>{item.igvPoints ? item.igvPoints : ''}</td>
                  <td style={{ padding: '0.25rem 0.4rem' }}>
                    {mode === 'rider' && item.horseRegisterId
                      ? <a href='#' onClick={(e) => { e.preventDefault(); onOpenOther(item.horseRegisterId!, 'horse', item.horseName ?? item.horseRegisterId!) }}>
                          {item.horseName ?? '-'}
                        </a>
                      : (item.horseName ?? '-')}
                  </td>
                  <td style={{ padding: '0.25rem 0.4rem' }}>
                    {mode === 'horse' && item.riderRegisterId
                      ? <a href='#' onClick={(e) => { e.preventDefault(); onOpenOther(item.riderRegisterId!, 'rider', riderName(item) || item.riderRegisterId!) }}>
                          {riderName(item) || '-'}
                        </a>
                      : (riderName(item) || '-')}
                  </td>
                  <td style={{ padding: '0.25rem 0.4rem' }}>
                    {item.showId && item.examId
                      ? <a href='#' onClick={(e) => { e.preventDefault(); onHide(); onNavigateToExam(item.showId!, item.examId!) }}
                           style={{ fontWeight: 500 }}>
                          {item.examName ?? '-'}{item.examType ? ' [' + item.examType + ']' : ''}
                        </a>
                      : <span>{item.examName ?? '-'}{item.examType ? ' [' + item.examType + ']' : ''}</span>}
                    {item.showName
                      ? <div style={{ fontSize: '0.78rem', color: '#888' }}>
                          {item.showName}{item.showYear ? ' (' + item.showYear + ')' : ''}
                        </div>
                      : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Dialog>
  )
}
